class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = this.querySelector('#cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.requestVersion = 0;

    this.notification.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelectorAll('[data-cart-notification-close]').forEach((button) =>
      button.addEventListener('click', this.close.bind(this))
    );

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rusty-add]');
      if (button && this.contains(button)) this.addSuggestion(button);
    });
  }

  open() {
    this.notification.classList.add('animate', 'active');
    this.notification.focus();
    trapFocus(this.notification);
    document.body.addEventListener('click', this.onBodyClick);
    this.dispatchCartViewEvent();
  }

  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      if (!response.ok) return;
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (error) {
      return;
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    this.recommendationController?.abort();
    this.requestVersion += 1;
    removeTrapFocus(this.activeElement);
  }

  disconnectedCallback() {
    document.body.removeEventListener('click', this.onBodyClick);
    this.recommendationController?.abort();
    this.requestVersion += 1;
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key || parsedState.items?.[0]?.key;

    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!target || typeof html !== 'string') return;

      const content = this.getSectionInnerHTML(html, section.selector);
      if (content !== null) target.innerHTML = content;
    });

    if (this.header) this.header.reveal();
    this.open();
    this.loadSuggestions(parsedState);
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: `[id="cart-notification-product-${this.cartItemKey}"]` },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  productSectionUrl(line) {
    if (!line?.url) return null;
    const url = new URL(line.url, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    url.searchParams.set('variant', line.variant_id);
    url.searchParams.set('section_id', 'rusty-cart-recommendations');
    return url;
  }

  recommendationUrl(productId, intent) {
    if (!productId || !this.dataset.recommendationsUrl) return null;
    const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
    url.searchParams.set('section_id', 'rusty-cart-recommendations');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);
    return url;
  }

  cartContainsSurfboard(cart) {
    return cart.items.some((item) => {
      const type = String(item.product_type || '').toLowerCase();
      const title = String(item.product_title || '').toLowerCase();
      const accessory = /fin|leash|leg rope|pad|traction|bag|cover|sock/.test(`${type} ${title}`);
      return type.includes('surfboard') || (!accessory && /shortboard|midlength|surfboard/.test(title));
    });
  }

  cardsFromHtml(html, inCart, cartHasSurfboard) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const cards = [];

    for (const card of documentFragment.querySelectorAll('[data-rusty-cart-results] [data-product-id]')) {
      const productId = card.dataset.productId;
      if (!productId || inCart.has(productId)) continue;
      if (cartHasSurfboard && card.dataset.productKind === 'surfboard') continue;

      inCart.add(productId);
      cards.push(card);
      if (cards.length === 3) break;
    }

    return cards;
  }

  async loadSuggestions(addedItem) {
    const panel = this.querySelector('[data-rusty-upsell]');
    const list = this.querySelector('[data-rusty-suggestions]');
    const status = this.querySelector('[data-rusty-status]');
    if (!panel || !list || !status) return;

    this.recommendationController?.abort();
    const controller = new AbortController();
    this.recommendationController = controller;
    const version = ++this.requestVersion;

    panel.hidden = true;
    list.replaceChildren();
    status.textContent = '';

    const timer = setTimeout(() => controller.abort(), 8000);

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`, { signal: controller.signal });
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const line = cart.items.find((item) => item.key === this.cartItemKey) || cart.items[0];
      const productId = line?.product_id || addedItem.product_id || addedItem.items?.[0]?.product_id;
      if (!productId) return;

      const urls = [
        this.productSectionUrl(line),
        this.recommendationUrl(productId, 'complementary'),
        this.recommendationUrl(productId, 'related'),
      ].filter(Boolean);

      const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));
      cartProductIds.add(String(productId));
      const cartHasSurfboard = this.cartContainsSurfboard(cart);
      let cards = [];

      for (const url of urls) {
        const response = await fetch(url.toString(), { signal: controller.signal });
        if (!response.ok) continue;
        cards = this.cardsFromHtml(await response.text(), new Set(cartProductIds), cartHasSurfboard);
        if (cards.length) break;
      }

      if (version !== this.requestVersion || !this.notification.classList.contains('active')) return;

      list.replaceChildren(...cards);
      panel.hidden = cards.length === 0;

      if (!panel.hidden) {
        const focused = this.notification.contains(document.activeElement)
          ? document.activeElement
          : this.notification;
        trapFocus(this.notification, focused);
      }
    } catch (error) {
      if (error.name !== 'AbortError') status.textContent = '';
    } finally {
      clearTimeout(timer);
    }
  }

  async addSuggestion(button) {
    if (this.addingSuggestion || button.disabled) return;

    this.addingSuggestion = true;
    button.disabled = true;
    const originalLabel = button.textContent;
    const status = this.querySelector('[data-rusty-status]');
    button.textContent = 'Adding…';
    status.textContent = '';
    let added = false;

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: button.dataset.rustyAdd,
          quantity: 1,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        status.textContent =
          typeof result.description === 'string'
            ? result.description
            : 'This item could not be added. Please check its product page.';
        button.disabled = false;
        return;
      }

      added = true;
      if (this.notification.classList.contains('active')) this.renderContents(result);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-notification',
          productVariantId: result.variant_id || result.id,
          cartData: result,
        });
      }
    } catch (error) {
      status.textContent = added
        ? 'Item added. Open your cart to check it.'
        : 'We could not confirm the addition. Check your cart before trying again.';
    } finally {
      this.addingSuggestion = false;
      button.textContent = added ? 'Added' : originalLabel;
    }
  }

  handleBodyClick(event) {
    const target = event.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : this.activeElement;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
